// src/pages/driver_group/DriverGroupsPage.js - Fixed Status dropdown label overlap issue
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Box, 
  Typography, 
  Button, 
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Paper,
} from '@mui/material';
import { Link } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import SearchIcon from '@mui/icons-material/Search';
import { getDriverGroups, deleteDriverGroup } from '../../api/driverGroupAPI';
import { getCompanies } from '../../api/companyAPI';
import LoadingSpinner from '../../components/common/Loadingspinner';
import ErrorAlert from '../../components/common/ErrorAlert';
import DriverGroupList from '../../components/driver_group/DriverGroupList';
import { getTariffs } from '../../api/tariffAPI';  
const DriverGroupsPage = () => {
  const queryClient = useQueryClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [filters, setFilters] = useState({
    companyId: '',
    enabled: '',
    search: ''
  });

  // Fetch all companies for filter dropdown
  const { data: companies, isLoading: isLoadingCompanies } = useQuery({
    queryKey: ['companies'],
    queryFn: () => getCompanies()
  });

  // Add this query after the companies query
const { data: tariffs } = useQuery({
  queryKey: ['tariffs'],
  queryFn: () => getTariffs()
});

// Add this helper function
const getTariffName = (tariffId) => {
  const tariff = tariffs?.find(t => t.TariffsId === tariffId);
  return tariff ? `${tariff.TariffsName} (${tariff.TariffsType || 'No type'})` : `Tariff ID: ${tariffId}`;
};

  // Set default company ID when companies are loaded
  useEffect(() => {
    if (companies && companies.length > 0 && !filters.companyId) {
      setFilters(prev => ({
        ...prev,
        companyId: companies[0].CompanyId.toString()
      }));
    }
  }, [companies]);

  // Fetch driver groups based on filters
  const { data: driverGroups, isLoading, isError, error } = useQuery({
    queryKey: ['driver-groups', filters],
    queryFn: () => {
      if (filters.companyId) {
        return getDriverGroups(
          parseInt(filters.companyId),
          filters.enabled !== '' ? filters.enabled === 'true' : undefined
        );
      }
      return [];
    },
    enabled: !!filters.companyId
  });

  // Delete driver group mutation
  const deleteMutation = useMutation({
    mutationFn: deleteDriverGroup,
    onSuccess: () => {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['driver-groups'] });
      
      setSnackbar({
        open: true,
        message: 'Driver group deleted successfully',
        severity: 'success'
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error deleting driver group: ${error.message}`,
        severity: 'error'
      });
    }
  });

  const handleDeleteClick = (groupId) => {
    setGroupToDelete(groupId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (groupToDelete) {
      try {
        await deleteMutation.mutateAsync(groupToDelete);
      } catch (err) {
        // Error is handled by the mutation
      }
      setDeleteDialogOpen(false);
      setGroupToDelete(null);
    }
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Filter driver groups by search term
  const filteredDriverGroups = driverGroups && driverGroups.filter(group => {
    if (!filters.search) return true;
    
    const searchTerm = filters.search.toLowerCase();
    return group.DriversGroupName.toLowerCase().includes(searchTerm);
  });

  // Find the current company name
  const currentCompany = companies?.find(company => company.CompanyId.toString() === filters.companyId);
  const currentCompanyName = currentCompany?.CompanyName || "Unknown Company";

  if (isLoading || isLoadingCompanies) {
    return <LoadingSpinner />;
  }

  if (isError) {
    return <ErrorAlert message={error instanceof Error ? error.message : 'Failed to load driver groups'} />;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Driver Groups</Typography>
        <Button 
          component={Link} 
          to="/driver_groups/new" 
          variant="contained" 
          startIcon={<AddIcon />}
        >
          Add Driver Group
        </Button>
      </Box>

      {/* Filters */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              name="search"
              label="Search Driver Groups"
              variant="outlined"
              fullWidth
              value={filters.search}
              onChange={handleFilterChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="medium"
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="company-filter-label">Company</InputLabel>
              <Select
                labelId="company-filter-label"
                name="companyId"
                value={filters.companyId}
                label="Company"
                onChange={handleFilterChange}
                startAdornment={
                  <InputAdornment position="start">
                    <BusinessIcon />
                  </InputAdornment>
                }
              >
                {!isLoadingCompanies && companies && companies.map((company) => (
                  <MenuItem key={company.CompanyId} value={company.CompanyId.toString()}>
                    {company.CompanyName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="status-filter-label" shrink>Status</InputLabel>
              <Select
                labelId="status-filter-label"
                name="enabled"
                value={filters.enabled}
                label="Status"
                onChange={handleFilterChange}
                displayEmpty
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="true">Enabled</MenuItem>
                <MenuItem value="false">Disabled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Company information display */}
      <Box mb={3}>
        <Typography variant="h6">
          Driver Groups for: {currentCompanyName}
        </Typography>
      </Box>

      {/* Driver Groups List */}
      <DriverGroupList
        driverGroups={filteredDriverGroups}
        companyId={parseInt(filters.companyId)}
        isLoading={isLoading}
        isError={isError}
        error={error}
        onDeleteClick={handleDeleteClick}
        getTariffName={getTariffName}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Driver Group</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this driver group? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DriverGroupsPage;